import { cn } from '@/lib/shadcn/utils';
import {
  type CandidateDetailData,
  type CandidateTab,
  candidateTabs,
} from '@/modules/candidate-detail/types';
import type { CandidateStatusApi } from '@/modules/candidates/types';

import { CandidateActivity } from './CandidateActivity';
import { CandidateAsync } from './CandidateAsync';
import { CandidateExperience } from './CandidateExperience';
import { CandidatePrescreen } from './CandidatePrescreen';
import { CandidateSummary } from './CandidateSummary';

const tabLabels: Record<CandidateTab, string> = {
  summary: 'Summary',
  prescreen: 'Pre-screen',
  experience: 'Experience',
  activity: 'Activity',
};

interface CandidateEvidenceTabsProps {
  candidate: CandidateDetailData;
  jobId: string;
  selectedTab: CandidateTab;
  status: CandidateStatusApi | null;
  onTabChange: (tab: CandidateTab) => void;
}

/** The right column: tabs on a hairline, then the selected evidence. */
export function CandidateEvidenceTabs({
  candidate,
  jobId,
  selectedTab,
  status,
  onTabChange,
}: CandidateEvidenceTabsProps) {
  const params = { candidateId: candidate.candidateId, jobId };
  const resetKeys = [candidate.candidateId, jobId];

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <nav
        aria-label="Candidate detail sections"
        className="flex gap-6 border-b"
      >
        {candidateTabs.map((tab) => {
          const isCurrent = tab === selectedTab;
          return (
            <button
              aria-current={isCurrent ? 'page' : undefined}
              className={cn(
                'focus-visible:ring-ring/50 -mb-px cursor-pointer border-b pb-2.5 text-[13px] font-medium transition-colors duration-100 outline-none focus-visible:ring-[3px]',
                isCurrent
                  ? 'border-foreground text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
              key={tab}
              onClick={() => onTabChange(tab)}
              type="button"
            >
              {tabLabels[tab]}
            </button>
          );
        })}
      </nav>

      {selectedTab === 'summary' && <CandidateSummary candidate={candidate} />}
      {selectedTab === 'prescreen' && (
        <CandidatePrescreen
          candidate={candidate}
          jobId={jobId}
          status={status}
        />
      )}
      {selectedTab === 'experience' && (
        <CandidateAsync
          errorDescription="We could not load this candidate’s work experience. Check your connection and try again."
          errorTitle="Experience unavailable"
          loadingDescription="Loading career history…"
          resetKeys={resetKeys}
        >
          <CandidateExperience params={params} />
        </CandidateAsync>
      )}
      {selectedTab === 'activity' && (
        <CandidateAsync
          errorDescription="We could not load this candidate’s activity. Check your connection and try again."
          errorTitle="Activity unavailable"
          loadingDescription="Loading activity…"
          resetKeys={resetKeys}
        >
          <CandidateActivity firstName={candidate.firstName} params={params} />
        </CandidateAsync>
      )}
    </div>
  );
}
